import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import { useAuth, UserButton, useUser } from "@clerk/clerk-react";
import {
  Organisation,
  PlanRun,
  TaskDefinition,
  TaskStatus,
  TaskType,
  Team,
  User as TPUser,
} from "../types";
import {
  completeTask,
  forceStartRun,
  getRunnerContext,
  retryTask,
  skipTask,
} from "../services/storageService";
import {
  calculateProgress,
  calculateProjectedEndTime,
  formatDuration as formatDurationService,
} from "../services/schedulingService";
import { useRunWebSocket } from "./useRunWebSocket"; // Import the hook
import { RichTextEditor, RichTextViewer } from "./RichTextEditor";
import TimelineView from "./TimelineView";
import PlanFlowRunner from "./PlanFlowRunner";
import { SmartSuggestions } from "./SmartSuggestions";
import { Modal } from "./Modals";
import confetti from "canvas-confetti";
import {
  AlertCircle,
  Calendar,
  CheckCircle,
  ChevronRight,
  Clock,
  Cpu,
  FileText,
  GanttChartSquare,
  Github,
  List,
  SkipForward,
  User as UserIcon,
  Users as UsersIcon,
  Workflow,
  XCircle,
  Zap,
} from "./Icons";

interface PlanRunnerProps {
  currentOrg: Organisation;
  onClose: () => void;
}

const getTaskNumber = (tasks: TaskDefinition[], taskId: string) => {
  let count = 0;
  for (const t of tasks) {
    if (t.type !== TaskType.SECTION) {
      count++;
    }
    if (t.id === taskId) {
      return t.type === TaskType.SECTION ? null : count;
    }
  }
  return null;
};

const PlanRunner: React.FC<PlanRunnerProps> = ({ currentOrg, onClose }) => {
  const { runId } = useParams<{ runId: string }>();
  const { user } = useUser();
  const { getToken } = useAuth();
  const [viewMode, setViewMode] = useState<"LIST" | "TIMELINE" | "FLOW" | "SUMMARY">(
    "LIST",
  );
  const [run, setRun] = useState<PlanRun | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [taskNotes, setTaskNotes] = useState<Record<string, string>>({});
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [userTeams, setUserTeams] = useState<Team[]>([]);
  const [allTeams, setAllTeams] = useState<Team[]>([]);
  const [allUsers, setAllUsers] = useState<TPUser[]>([]);
  const [activeUsers, setActiveUsers] = useState<
    { userId: string; name: string; initials: string; imageUrl?: string }[]
  >([]);
  const [activeAction, setActiveAction] = useState<{
    taskId: string;
    type: "COMPLETE" | "SKIP";
  } | null>(null);
  const [checkedPrerequisites, setCheckedPrerequisites] = useState<Set<number>>(
    new Set(),
  );

  const [isRunLoading, setIsRunLoading] = useState(true);
  const [isContextLoading, setIsContextLoading] = useState(true);
  const isLoading = isRunLoading || isContextLoading;

  const fetchContextRef = useRef(false);
  const confettiFiredRef = useRef(false);
  const initialStatusRef = useRef<string | null>(null);
  const hasInitializedViewMode = useRef(false);

  useEffect(() => {
    if (run && !hasInitializedViewMode.current) {
      if (run.status === "COMPLETED") {
        setViewMode("SUMMARY");
      }
      hasInitializedViewMode.current = true;
    }
  }, [run]);

  useEffect(() => {
    if (hasInitializedViewMode.current && run?.status === "COMPLETED") {
      setViewMode("SUMMARY");
    }
  }, [run?.status]);

  // Track initial status to avoid firing confetti on load
  useEffect(() => {
    if (run?.status && initialStatusRef.current === null) {
      initialStatusRef.current = run.status;
    }
  }, [run?.status]);

  // Confetti when finished
  useEffect(() => {
    if (
      run?.status === "COMPLETED" &&
      initialStatusRef.current === "RUNNING" &&
      run.taskStates &&
      !confettiFiredRef.current
    ) {
      const states = Object.values(run.taskStates);
      const allSuccessful = states.every(
        (s) => s.status === TaskStatus.COMPLETED,
      );
      if (allSuccessful && states.length > 0) {
        confettiFiredRef.current = true;
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 },
        });
      }
    }
  }, [run?.status, run?.taskStates]);

  const isMaintainer = currentOrg.role === "MAINTAINER";
  const isTrialExpired =
    currentOrg.subscriptionStatus === "TRIALING" &&
    (currentOrg.subscriptionCurrentPeriodEnd || 0) <= Date.now();
  const isReadOnly = isTrialExpired;

  const canManage =
    !isReadOnly &&
    (isMaintainer || (run && run.planSnapshot.ownerId === user?.id));

  const resolveAssigneeInfo = (type: "USER" | "TEAM", id: string) => {
    if (type === "USER") {
      if (user && user.id === id) {
        return {
          name: user.fullName || user.firstName || "Me",
          imageUrl: user.imageUrl,
          initials:
            (
              (user.firstName?.[0] || "") + (user.lastName?.[0] || "")
            ).toUpperCase() || "??",
        };
      }
      // Check resolved users from API snapshot context
      if (run?.resolvedUsers) {
        const ru = run.resolvedUsers.find((u) => u.id === id);
        if (ru) {
          return {
            name: ru.name,
            imageUrl: ru.imageUrl,
            initials: ru.initials,
          };
        }
      }
      const u = allUsers.find((u) => u.id === id);
      return {
        name: u?.name || "Unknown user",
        imageUrl: u?.imageUrl,
        initials: u?.initials || "??",
      };
    } else {
      const t = allTeams.find((t) => t.id === id);
      return {
        name: t?.name || "Unknown team",
        isTeam: true,
      };
    }
  };

  const handleRunUpdate = useCallback((updatedRun: PlanRun) => {
    setRun(updatedRun);
  }, []);

  const handlePresenceUpdate = useCallback((presence: any[]) => {
    setActiveUsers([
      ...new Map(presence.map((item) => [item.userId, item])).values(),
    ]);
  }, []);

  const handleChecklistUpdate = useCallback((checklist: number[]) => {
    setCheckedPrerequisites(new Set(checklist));
  }, []);

  const { isConnected, togglePrerequisite } = useRunWebSocket({
    runId,
    user,
    onRunUpdate: handleRunUpdate,
    onPresenceUpdate: handlePresenceUpdate,
    onChecklistUpdate: handleChecklistUpdate,
  });

  const isAssigned = (task: TaskDefinition) => {
    if (canManage) return true;
    if (!user) return false;

    const assignees = task.assignees || [];
    if (assignees.some((a) => a.type === "USER" && a.id === user.id))
      return true;

    const myTeamIds = new Set(userTeams.map((t) => t.id));
    if (assignees.some((a) => a.type === "TEAM" && myTeamIds.has(a.id)))
      return true;

    return false;
  };

  const fetchContext = useCallback(async () => {
    if (!runId) return;
    if (!run) {
      setIsRunLoading(true);
      setIsContextLoading(true);
    }
    try {
      const token = await getToken();
      const data = await getRunnerContext(
        currentOrg.id,
        runId,
        token || undefined,
      );

      setRun(data.run);
      setAllTeams(data.teams);
      setAllUsers(data.members);

      const myTeams = data.teams.filter((t) =>
        t.members.some((m) => m.userId === user?.id),
      );
      setUserTeams(myTeams);
    } catch (e) {
      if (!run) setError("Run not found or failed to load context");
    } finally {
      setIsRunLoading(false);
      setIsContextLoading(false);
    }
  }, [runId, getToken, currentOrg.id, user?.id, run]);

  // Initial Fetch
  useEffect(() => {
    if (fetchContextRef.current) return;
    fetchContextRef.current = true;
    fetchContext();
  }, [fetchContext]);

  useEffect(() => {
    const timer = setInterval(() => {
      if (run?.status !== "COMPLETED") {
        setCurrentTime(Date.now());
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [run?.status]);

  const formatTime = (epoch: any) => {
    if (!epoch) return "--:--";
    const date = new Date(Number(epoch));
    if (isNaN(date.getTime())) return "--:--";
    const isToday = new Date().toDateString() === date.toDateString();
    const timeStr = date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
    return isToday
      ? timeStr
      : `${date.toLocaleDateString([], { month: "short", day: "numeric" })} ${timeStr}`;
  };

  const formatDuration = (start: number, end: number) => {
    return formatDurationService(Math.floor((end - start) / 60000));
  };

  const { projectedEndTime, progressPercent } = useMemo(() => {
    if (!run) return { projectedEndTime: 0, progressPercent: 0 };
    const end =
      run.status === "COMPLETED"
        ? run.endTime || currentTime
        : calculateProjectedEndTime(
            run.planSnapshot.tasks,
            run.taskStates,
            run.startTime,
          );
    let pct = 0;
    if (run.status === "COMPLETED") {
      pct = 100;
    } else if (run.status === "RUNNING") {
      pct = calculateProgress(
        run.planSnapshot.tasks,
        run.taskStates,
        run.startTime,
        currentTime,
      );
    }
    return { projectedEndTime: end, progressPercent: pct };
  }, [run, currentTime]);

  const handleManualComplete = async (taskId: string) => {
    if (!runId || !run) return;

    // Optimistic update
    setRun(prev => prev ? {
      ...prev,
      taskStates: {
        ...prev.taskStates,
        [taskId]: {
          ...prev.taskStates[taskId],
          status: TaskStatus.COMPLETED,
          notes: taskNotes[taskId]
        }
      }
    } : null);

    try {
      const token = await getToken();
      await completeTask(
        runId,
        taskId,
        token || undefined,
        taskNotes[taskId] || undefined,
      );
    } catch (e) {
      // Revert if API fails (could do this by refetching context)
      fetchContext(); 
      alert("Failed to complete task: " + e);
    }
  };

  const handleSkip = async (taskId: string) => {
    if (!runId || !run) return;

    // Optimistic update
    setRun(prev => prev ? {
      ...prev,
      taskStates: {
        ...prev.taskStates,
        [taskId]: {
          ...prev.taskStates[taskId],
          status: TaskStatus.SKIPPED,
          notes: taskNotes[taskId]
        }
      }
    } : null);

    try {
      const token = await getToken();
      await skipTask(
        runId,
        taskId,
        token || undefined,
        taskNotes[taskId] || undefined,
      );
    } catch (e) {
      // Revert if API fails
      fetchContext();
      alert("Failed to skip task: " + e);
    }
  };
  const handleForceStart = async () => {
    if (!runId) return;
    try {
      const token = await getToken();
      await forceStartRun(runId, token || undefined);
      // No refetch needed
    } catch (e) {
      alert("Failed to start run: " + e);
    }
  };

  const tpUser: TPUser | null = user
    ? {
        id: user.id,
        name: user.fullName || "User",
        email: user.primaryEmailAddress?.emailAddress || "",
        initials: user.firstName?.[0] || "U",
        imageUrl: user.imageUrl,
        organisationIds: [],
      }
    : null;

  if (error)
    return (
      <div
        data-testid="plan-runner-error"
        className="p-8 text-red-600 dark:text-red-400"
      >
        {error}
      </div>
    );
  if (isLoading || !run || !tpUser) {
    return (
      <div
        data-testid="loading-run"
        className="min-h-screen bg-slate-50 dark:bg-slate-950 flex flex-col animate-pulse"
      >
        <div className="h-16 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800"></div>
        <div className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 h-24"></div>
        <div className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full space-y-4">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="h-24 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800"
            ></div>
          ))}
        </div>
      </div>
    );
  }

  if (run.status === "SCHEDULED") {
    const timeLeft = Math.max(0, (run.scheduledStartTime || 0) - currentTime);
    const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
    const hours = Math.floor(
      (timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
    );
    const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);

    const prerequisites = run.planSnapshot.prerequisites || [];
    const allPrerequisitesChecked =
      prerequisites.length === 0 ||
      prerequisites.every((_, i) => checkedPrerequisites.has(i));
    const isImmediate = (run.scheduledStartTime || 0) <= currentTime;

    return (
      <div
        data-testid="plan-runner-scheduled"
        className="flex flex-col h-full items-center justify-center bg-slate-50 dark:bg-slate-900 p-6 text-center overflow-y-auto"
      >
        <div className="bg-white dark:bg-slate-800 p-10 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-700 max-w-lg w-full my-auto">
          <div className="flex justify-center mb-6">
            <div className="p-4 bg-indigo-50 dark:bg-indigo-900/30 rounded-full">
              <Calendar className="text-indigo-600 dark:text-indigo-400 w-10 h-10" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-2">
            {run.name}
          </h2>
          <p className="text-slate-500 dark:text-slate-400 mb-8">
            {isImmediate
              ? "Ready to start"
              : `Scheduled for ${new Date(run.scheduledStartTime || 0).toLocaleString()}`}
          </p>

          {!isImmediate && (
            <div className="flex gap-4 justify-center text-slate-700 dark:text-slate-200 font-mono text-2xl mb-8">
              {days > 0 && (
                <>
                  <div className="bg-slate-100 dark:bg-slate-700 p-3 rounded">
                    {String(days).padStart(2, "0")}{" "}
                    <span className="text-xs block text-slate-400">DAYS</span>
                  </div>
                  <div className="self-center">:</div>
                </>
              )}
              {hours > 0 && (
                <>
                  <div className="bg-slate-100 dark:bg-slate-700 p-3 rounded">
                    {String(hours).padStart(2, "0")}{" "}
                    <span className="text-xs block text-slate-400">HRS</span>
                  </div>
                  <div className="self-center">:</div>
                </>
              )}
              <div className="bg-slate-100 dark:bg-slate-700 p-3 rounded">
                {String(minutes).padStart(2, "0")}{" "}
                <span className="text-xs block text-slate-400">MIN</span>
              </div>
              <div className="self-center">:</div>
              <div className="bg-slate-100 dark:bg-slate-700 p-3 rounded">
                {String(seconds).padStart(2, "0")}{" "}
                <span className="text-xs block text-slate-400">SEC</span>
              </div>
            </div>
          )}

          {prerequisites.length > 0 && (
            <div className="mb-8 text-left">
              <h3 className="text-sm font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-3">
                Pre-run Checklist
              </h3>
              <div className="space-y-2 bg-slate-50 dark:bg-slate-900/50 p-4 rounded-xl border border-slate-100 dark:border-slate-800">
                {prerequisites.map((item, idx) => (
                  <label
                    key={idx}
                    className="flex items-start gap-3 cursor-pointer p-1 hover:bg-slate-100 dark:hover:bg-slate-800/50 rounded transition"
                  >
                    <div className="relative flex items-center mt-0.5">
                      <input
                        type="checkbox"
                        checked={checkedPrerequisites.has(idx)}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          const newSet = new Set(checkedPrerequisites);
                          if (checked) newSet.add(idx);
                          else newSet.delete(idx);
                          setCheckedPrerequisites(newSet);
                          togglePrerequisite(idx, checked);
                        }}
                        disabled={!canManage}
                        className="peer h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-600 dark:border-slate-600 dark:bg-slate-800 dark:ring-offset-slate-900"
                      />
                    </div>
                    <span
                      className={`text-sm ${checkedPrerequisites.has(idx) ? "text-slate-400 line-through" : "text-slate-700 dark:text-slate-200"}`}
                    >
                      {item}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-3">
            {canManage && (
              <button
                onClick={handleForceStart}
                className="w-full py-3 bg-indigo-600 text-white rounded-lg font-bold hover:bg-indigo-700 transition"
              >
                Start Now
              </button>
            )}
            <button
              onClick={onClose}
              className="w-full py-3 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 rounded-lg font-bold hover:bg-slate-50 dark:hover:bg-slate-700 transition"
            >
              Close & Wait
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="plan-runner-container"
      className="h-screen bg-slate-50 dark:bg-slate-950 transition-colors duration-200 flex flex-col overflow-hidden"
    >
      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 sticky top-0 z-header backdrop-blur-sm bg-white/80 dark:bg-slate-900/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div
            className="flex items-center gap-2 cursor-pointer"
            onClick={onClose}
          >
            <div className="bg-indigo-600 rounded-lg p-1.5">
              <Zap className="h-5 w-5 text-white" />
            </div>
            <span className="font-bold text-xl text-slate-900 dark:text-white tracking-tight">
              taskplan
            </span>
            <span className="text-slate-300 dark:text-slate-600 mx-2">/</span>
            <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
              <span
                data-testid="run-name"
                className="font-medium text-slate-700 dark:text-slate-200"
              >
                {run.name}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <button
              onClick={onClose}
              className="text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-white transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </header>

      <div className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 shadow-sm z-sticky sticky top-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-6 w-full sm:w-auto justify-between sm:justify-start">
            <div className="flex items-center gap-3">
              {run.status === "COMPLETED" && (
                <h1
                  data-testid="run-status-heading"
                  className="hidden md:block text-xl font-bold text-slate-900 dark:text-white"
                >
                  Completed
                </h1>
              )}
              {run.status === "RUNNING" && (
                <span className="relative flex h-3 w-3 ml-3" title="Running...">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                </span>
              )}
            </div>

            {run.status !== "COMPLETED" && (
              <div className="flex items-center gap-1 border-l border-slate-200 dark:border-slate-800 pl-6">
                {/* Current User */}
                <UserButton afterSignOutUrl="/" />
                {/* Active Users */}
                {activeUsers.filter((u) => u.userId !== tpUser.id).length >
                  0 && (
                  <div className="flex -space-x-2">
                    {activeUsers
                      .filter((u) => u.userId !== tpUser.id) // Show others
                      .slice(0, 3)
                      .map((u, i) => (
                        <div
                          key={u.userId}
                          className="w-8 h-8 flex items-center justify-center text-xs font-bold text-slate-600 dark:text-slate-300 relative group cursor-default overflow-hidden"
                          title={u.name}
                          style={{ zIndex: 10 - i }}
                        >
                          {u.imageUrl ? (
                            <img
                              src={u.imageUrl}
                              alt={u.name}
                              className="w-full h-full rounded-full bg-slate-200 dark:bg-slate-700 border-2 border-white dark:border-slate-900 object-cover"
                            />
                          ) : (
                            <div className="w-full h-full rounded-full bg-slate-200 dark:bg-slate-700 border-2 border-white dark:border-slate-900 flex items-center justify-center">
                              {u.initials}
                            </div>
                          )}
                          {/* Online dot */}
                          <div className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 border-2 border-white dark:border-slate-900 rounded-full"></div>
                        </div>
                      ))}
                    {activeUsers.filter((u) => u.userId !== tpUser.id).length >
                      3 && (
                      <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 border-2 border-white dark:border-slate-900 flex items-center justify-center text-xs font-medium text-slate-500 relative group cursor-default">
                        +
                        {activeUsers.filter((u) => u.userId !== tpUser.id)
                          .length - 3}
                        {/* Hover Details */}
                        <div className="absolute top-full mt-2 right-0 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl p-2 w-48 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity z-50">
                          <div className="text-[10px] font-bold text-slate-400 uppercase mb-2 px-1">
                            Also Online
                          </div>
                          <div className="space-y-1">
                            {activeUsers
                              .filter((u) => u.userId !== tpUser.id)
                              .slice(3)
                              .map((u) => (
                                <div
                                  key={u.userId}
                                  className="flex items-center gap-2 p-1 hover:bg-slate-50 dark:hover:bg-slate-700/50 rounded transition"
                                >
                                  <div className="w-5 h-5 rounded-full overflow-hidden flex-shrink-0 bg-indigo-500 flex items-center justify-center text-[8px] text-white">
                                    {u.imageUrl ? (
                                      <img
                                        src={u.imageUrl}
                                        className="w-full h-full object-cover"
                                      />
                                    ) : (
                                      u.initials
                                    )}
                                  </div>
                                  <span className="text-xs text-slate-700 dark:text-slate-200 truncate">
                                    {u.name}
                                  </span>
                                </div>
                              ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-6 w-full justify-center sm:w-auto">
            <div className="flex-1 md:w-80 hidden lg:block">
              <div className="flex justify-between text-xs text-slate-500 dark:text-slate-400 mb-1.5 font-mono">
                <span>{formatTime(run.startTime)}</span>
                <span className="font-bold text-slate-700 dark:text-slate-200">
                  {Math.round(progressPercent)}%
                </span>
                <span>{formatTime(projectedEndTime)}</span>
              </div>
              <div className="h-2.5 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden border border-slate-200 dark:border-slate-700">
                <div
                  className="h-full bg-indigo-500 transition-all duration-500 ease-out shadow-sm"
                  style={{ width: `${progressPercent}%` }}
                ></div>
              </div>
            </div>
            <div className="flex bg-slate-100 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-1 shadow-sm shrink-0">
              <button
                onClick={() => setViewMode("LIST")}
                className={`px-3 py-1.5 rounded-md flex items-center gap-2 text-xs font-medium transition-colors ${viewMode === "LIST" ? "bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm" : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"}`}
              >
                <List size={14} /> List
              </button>
              <button
                onClick={() => setViewMode("TIMELINE")}
                className={`px-3 py-1.5 rounded-md flex items-center gap-2 text-xs font-medium transition-colors ${viewMode === "TIMELINE" ? "bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm" : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"}`}
              >
                <GanttChartSquare size={14} /> Timeline
              </button>
              <button
                onClick={() => setViewMode("FLOW")}
                className={`px-3 py-1.5 rounded-md flex items-center gap-2 text-xs font-medium transition-colors ${viewMode === "FLOW" ? "bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm" : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"}`}
              >
                <Workflow size={14} /> Flow
              </button>
              {run.status === "COMPLETED" && (
                <button
                  onClick={() => setViewMode("SUMMARY")}
                  className={`px-3 py-1.5 rounded-md flex items-center gap-2 text-xs font-medium transition-colors ${viewMode === "SUMMARY" ? "bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm" : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"}`}
                >
                  <FileText size={14} /> Summary
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-hidden relative bg-slate-50 dark:bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 h-full flex flex-col">
          <div className="flex-1 min-h-0">
            {viewMode === "TIMELINE" ? (
              <div className="h-full bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
                <TimelineView
                  tasks={run.planSnapshot.tasks}
                  taskStates={run.taskStates}
                  startTime={run.startTime}
                  scheduledStartTime={run.scheduledStartTime}
                  estimatedStartTime={run.planSnapshot.estimatedStartTime}
                  currentTime={
                    run.status === "COMPLETED"
                      ? run.endTime || currentTime
                      : currentTime
                  }
                  progressPercent={progressPercent}
                />
              </div>
            ) : viewMode === "FLOW" ? (
              <div className="h-full bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden relative">
                <PlanFlowRunner plan={run.planSnapshot} run={run} />
              </div>
            ) : viewMode === "SUMMARY" ? (
              <div className="h-full overflow-y-auto">
                <SmartSuggestions
                  run={run}
                  onPlanUpdated={fetchContext}
                  canEdit={canManage}
                  getToken={getToken}
                />
              </div>
            ) : (
              <div className="h-full overflow-y-auto space-y-3">
                {run.planSnapshot.tasks.map((task, idx) => {
                  if (task.type === TaskType.SECTION) {
                    return (
                      <div
                        key={task.id}
                        className="pt-4 pb-1 border-b border-slate-200 dark:border-slate-800"
                      >
                        <h3 className="text-sm font-bold text-slate-800 dark:text-white uppercase tracking-tight">
                          {task.name}
                        </h3>
                      </div>
                    );
                  }
                  const state = run.taskStates[task.id] || {
                    status: TaskStatus.PENDING,
                    logs: [],
                    startTime: 0,
                    endTime: 0,
                  };
                  const isActive =
                    state.status === TaskStatus.RUNNING ||
                    state.status === TaskStatus.WAITING_FOR_USER;
                  const taskNumber = getTaskNumber(
                    run.planSnapshot.tasks,
                    task.id,
                  );
                  const assigned = isAssigned(task);

                  return (
                    <div
                      key={task.id}
                      className={`bg-white dark:bg-slate-900 rounded-xl border transition-all duration-300 ${isActive ? "border-indigo-500 shadow-md ring-1 ring-indigo-500/20 z-decorator relative" : "border-slate-200 dark:border-slate-800 shadow-sm opacity-90"}`}
                    >
                      <div className="p-4 flex items-start gap-4">
                        <div className="mt-1">
                          <div className="flex flex-col items-center gap-1">
                            {getStatusIcon(state.status, 20)}
                            <span className="text-[10px] font-mono font-bold text-slate-400">
                              #{taskNumber}
                            </span>
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between items-start gap-4">
                            <div className="flex-1 min-w-0">
                              <h3 className="font-bold text-base text-slate-900 dark:text-white flex items-center gap-2 mb-2">
                                {task.name}
                                {task.type === TaskType.AUTOMATED && (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 font-bold uppercase tracking-wider flex items-center gap-1 border border-purple-200 dark:border-purple-800">
                                    <Cpu size={10} /> Auto
                                  </span>
                                )}
                                {state.githubRunId && task.githubRepo && (
                                  <a
                                    href={`https://github.com/${task.githubRepo}/actions/runs/${state.githubRunId}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="ml-auto inline-flex items-center gap-1.5 px-2 py-1 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:bg-slate-200 dark:hover:bg-slate-700 transition"
                                  >
                                    <Github size={12} /> View Logs
                                  </a>
                                )}
                              </h3>

                              {task.description && (
                                <div className="text-slate-600 dark:text-slate-400 text-sm prose dark:prose-invert max-w-none mb-3">
                                  <RichTextViewer content={task.description} />
                                </div>
                              )}

                              {task.dependencyIds &&
                                task.dependencyIds.length > 0 && (
                                  <div className="mb-3 flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                                    <span className="font-medium text-slate-700 dark:text-slate-300">
                                      Prerequisites:
                                    </span>
                                    <div className="flex flex-wrap gap-1">
                                      {task.dependencyIds.map((depId) => {
                                        const isCompleted =
                                          run.taskStates[depId]?.status ===
                                          TaskStatus.COMPLETED;
                                        return (
                                          <span
                                            key={depId}
                                            className={`px-1.5 py-0.5 rounded text-[10px] font-mono border flex items-center gap-1 ${isCompleted ? "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800" : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700"}`}
                                          >
                                            #
                                            {getTaskNumber(
                                              run.planSnapshot.tasks,
                                              depId,
                                            )}
                                          </span>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}

                              {task.type === TaskType.MANUAL && (
                                <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                                  <span className="font-medium text-slate-700 dark:text-slate-300">
                                    Assignees:
                                  </span>
                                  <div className="flex flex-wrap gap-1">
                                    {task.assignees &&
                                    task.assignees.length > 0 ? (
                                      task.assignees.map((a, idx) => {
                                        const info = resolveAssigneeInfo(
                                          a.type,
                                          a.id,
                                        );
                                        return (
                                          <div
                                            key={`${a.id}-${idx}`}
                                            className="flex items-center gap-1.5 bg-slate-100 dark:bg-slate-800 pl-1 pr-2 py-0.5 rounded-full border border-slate-200 dark:border-slate-700 text-xs font-medium text-slate-700 dark:text-slate-300 shadow-sm"
                                          >
                                            {info.isTeam ? (
                                              <UsersIcon
                                                size={10}
                                                className="ml-1"
                                              />
                                            ) : (
                                              <div className="w-5 h-5 rounded-full flex items-center justify-center text-[8px] text-white font-bold overflow-hidden bg-indigo-500">
                                                {info.imageUrl ? (
                                                  <img
                                                    src={info.imageUrl}
                                                    alt={info.name}
                                                    className="w-full h-full object-cover"
                                                  />
                                                ) : (
                                                  info.initials
                                                )}
                                              </div>
                                            )}
                                            {info.name}
                                          </div>
                                        );
                                      })
                                    ) : (
                                      <span className="text-xs italic text-slate-400">
                                        Unassigned
                                      </span>
                                    )}
                                  </div>
                                </div>
                              )}

                              {state.notes && (
                                <div className="mt-4 p-3 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg">
                                  <div className="flex items-center gap-1.5 text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">
                                    <FileText size={12} /> Execution Notes
                                  </div>
                                  <div className="text-slate-700 dark:text-slate-300 text-sm prose prose-sm dark:prose-invert max-w-none max-h-32 overflow-y-auto">
                                    <RichTextViewer content={state.notes} />
                                  </div>
                                </div>
                              )}
                            </div>

                            <div className="text-right shrink-0 flex flex-col items-end gap-2">
                              <div
                                className={`px-2 py-0.5 my-0.5 rounded-full text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1 ${getStatusBadgeClass(state.status)}`}
                              >
                                {state.status.replace(/_/g, " ")}
                              </div>
                              {state.endTime && (
                                <div className="text-[10px] text-slate-400 font-mono flex items-center justify-end gap-1">
                                  <Clock size={10} />
                                  {formatDuration(
                                    state.startTime || 0,
                                    state.endTime,
                                  )}
                                </div>
                              )}

                              {/* Action Buttons Top Right */}
                              {state.status === TaskStatus.WAITING_FOR_USER &&
                                assigned && (
                                  <div className="flex flex-row gap-2 mt-1 animate-in fade-in slide-in-from-right-4">
                                    <button
                                      onClick={() =>
                                        setActiveAction({
                                          taskId: task.id,
                                          type: "SKIP",
                                        })
                                      }
                                      className="px-3 py-1.5 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-md hover:bg-slate-50 dark:hover:bg-slate-700 shadow-sm transition text-xs font-bold uppercase tracking-wide"
                                    >
                                      Skip
                                    </button>
                                    <button
                                      onClick={() =>
                                        setActiveAction({
                                          taskId: task.id,
                                          type: "COMPLETE",
                                        })
                                      }
                                      className="flex items-center gap-2 px-3 py-1.5 bg-green-600 text-white rounded-md hover:bg-green-700 shadow-sm transition text-xs font-bold uppercase tracking-wide"
                                    >
                                      <CheckCircle size={14} /> Complete
                                    </button>
                                  </div>
                                )}
                            </div>
                          </div>

                          {isActive && state.status === TaskStatus.RUNNING && (
                            <div className="mt-3">
                              <div className="flex items-center justify-between text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-800/50 p-3 rounded-lg border border-slate-100 dark:border-slate-800">
                                <div className="flex items-center gap-3">
                                  <div className="relative">
                                    <div className="w-5 h-5 border-2 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
                                  </div>
                                  <span className="text-sm font-medium">
                                    Automated task in progress...
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}
                          {state.status === TaskStatus.FAILED && (
                            <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/30 rounded-lg text-sm flex items-center justify-between">
                              <div className="flex items-center gap-3 text-red-700 dark:text-red-400">
                                <AlertCircle size={16} className="shrink-0" />
                                <span className="font-medium text-xs">
                                  {state.error || "Task execution failed"}
                                </span>
                              </div>
                              <div className="flex gap-2">
                                <button
                                  disabled={!assigned}
                                  onClick={() => handleRetry(task.id)}
                                  className="px-2 py-1 bg-white dark:bg-slate-800 border border-red-200 dark:border-red-800/50 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-[10px] font-bold text-red-700 dark:text-red-400 uppercase tracking-wide transition disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  Retry
                                </button>
                                <button
                                  disabled={!assigned}
                                  onClick={() => handleSkip(task.id)}
                                  className="px-2 py-1 bg-white dark:bg-slate-800 border border-red-200 dark:border-red-800/50 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-[10px] font-bold text-red-700 dark:text-red-400 uppercase tracking-wide transition disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  Skip
                                </button>
                              </div>
                            </div>
                          )}
                          {(state.logs?.length > 0 || state.output) && (
                            <div className="mt-3 border-t border-slate-100 dark:border-slate-800 pt-2">
                              <details className="group">
                                <summary className="text-[10px] font-bold text-slate-500 dark:text-slate-400 cursor-pointer hover:text-indigo-600 dark:hover:text-indigo-400 select-none flex items-center gap-1 uppercase tracking-wider">
                                  <ChevronRight
                                    size={10}
                                    className="transition-transform group-open:rotate-90"
                                  />
                                  View Details
                                </summary>
                                <div className="mt-2 space-y-2 pl-3 border-l-2 border-slate-100 dark:border-slate-800">
                                  {state.logs?.length > 0 && (
                                    <div className="bg-slate-900 dark:bg-black text-slate-300 p-2 rounded-lg text-[10px] font-mono max-h-64 overflow-y-auto shadow-inner">
                                      {state.logs.map((log, i) => (
                                        <div
                                          key={i}
                                          className="py-0.5 border-b border-white/5 last:border-0"
                                        >
                                          <span className="text-slate-500 mr-2">
                                            $
                                          </span>
                                          {log}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  {state.output && (
                                    <div className="bg-slate-50 dark:bg-slate-800 p-3 rounded-lg text-xs text-slate-800 dark:text-slate-200 border border-slate-200 dark:border-slate-700">
                                      <div className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase mb-1 flex items-center gap-1">
                                        <FileText size={10} /> Output
                                      </div>
                                      <pre className="whitespace-pre-wrap font-sans">
                                        {state.output}
                                      </pre>
                                    </div>
                                  )}
                                </div>
                              </details>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {activeAction && run && (
        <Modal
          isOpen={!!activeAction}
          onClose={() => setActiveAction(null)}
          title={
            activeAction.type === "COMPLETE" ? "Complete Task" : "Skip Task"
          }
        >
          {(() => {
            const task = run.planSnapshot.tasks.find(
              (t) => t.id === activeAction.taskId,
            );
            const state = run.taskStates[activeAction.taskId];
            if (!task || !state) return null;

            const durationMs = currentTime - (state.startTime || currentTime);
            const durationMins = Math.floor(durationMs / 60000);
            const plannedMins = task.durationMinutes || 0;
            const isOverdue = plannedMins > 0 && durationMins > plannedMins;
            const timeDiff = Math.abs(durationMins - plannedMins);

            return (
              <div className="space-y-6">
                <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-xl border border-slate-100 dark:border-slate-800 flex items-center justify-between">
                  <div>
                    <div className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">
                      {state.endTime ? "Duration" : "Time Running"}
                    </div>
                    <div className="text-2xl font-mono font-bold text-slate-900 dark:text-white">
                      {Math.floor(durationMs / 1000 / 3600) > 0 ? (
                        <>
                          {Math.floor(durationMs / 1000 / 3600)}h{" "}
                          {Math.floor(((durationMs / 1000) % 3600) / 60)}m{" "}
                          {Math.floor((durationMs / 1000) % 60)}s
                        </>
                      ) : (
                        <>
                          {Math.floor(durationMs / 1000 / 60)}m{" "}
                          {Math.floor((durationMs / 1000) % 60)}s
                        </>
                      )}
                    </div>
                  </div>
                  {plannedMins > 0 && (
                    <div
                      className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wide border ${
                        isOverdue
                          ? "bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border-red-200 dark:border-red-800"
                          : timeDiff <= 1
                            ? "bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800"
                            : "bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 border-green-200 dark:border-green-800"
                      }`}
                    >
                      {isOverdue
                        ? `${timeDiff}m Overdue`
                        : timeDiff <= 1
                          ? "On Time"
                          : `${timeDiff}m Early`}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-bold text-slate-700 dark:text-slate-200 mb-2">
                    {activeAction.type === "COMPLETE"
                      ? "Notes (optional)"
                      : "Reason for skipping (optional)"}
                  </label>
                  <RichTextEditor
                    content={taskNotes[activeAction.taskId] || ""}
                    onChange={(val) =>
                      setTaskNotes((prev) => ({
                        ...prev,
                        [activeAction.taskId]: val,
                      }))
                    }
                    placeholder={
                      activeAction.type === "COMPLETE"
                        ? "Add notes about the execution..."
                        : "Why are you skipping this task?"
                    }
                    className="min-h-[120px]"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-2">
                  <button
                    onClick={() => setActiveAction(null)}
                    className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-sm font-medium transition"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      if (activeAction.type === "COMPLETE") {
                        handleManualComplete(activeAction.taskId);
                      } else {
                        handleSkip(activeAction.taskId);
                      }
                      setActiveAction(null);
                    }}
                    className={`px-4 py-2 text-white rounded-lg font-medium text-sm shadow-lg transition active:scale-95 ${
                      activeAction.type === "SKIP"
                        ? "bg-slate-600 hover:bg-slate-700 shadow-slate-500/20"
                        : "bg-green-600 hover:bg-green-700 shadow-green-500/20"
                    }`}
                  >
                    Confirm{" "}
                    {activeAction.type === "COMPLETE" ? "Complete" : "Skip"}
                  </button>
                </div>
              </div>
            );
          })()}
        </Modal>
      )}

      <style>{`
        @keyframes progress {
            0% { width: 0% }
            50% { width: 70% }
            100% { width: 90% }
        }
        .animate-progress {
            animation: progress 2s infinite ease-out;
        }
      `}</style>
    </div>
  );
};

const getStatusIcon = (status: TaskStatus, size: number = 24) => {
  switch (status) {
    case TaskStatus.COMPLETED:
      return (
        <CheckCircle
          className="text-green-500 dark:text-green-400"
          size={size}
        />
      );
    case TaskStatus.FAILED:
      return <XCircle className="text-red-500 dark:text-red-400" size={size} />;
    case TaskStatus.RUNNING:
      return (
        <div
          style={{ width: size, height: size }}
          className="rounded-full border-2 border-indigo-500 dark:border-indigo-400 border-t-transparent animate-spin"
        ></div>
      );
    case TaskStatus.WAITING_FOR_USER:
      return (
        <UserIcon className="text-blue-500 dark:text-blue-400" size={size} />
      );
    case TaskStatus.SKIPPED:
      return (
        <SkipForward
          className="text-slate-400 dark:text-slate-500"
          size={size}
        />
      );
    default:
      return (
        <Clock className="text-slate-300 dark:text-slate-600" size={size} />
      );
  }
};

const getStatusBadgeClass = (status: TaskStatus) => {
  switch (status) {
    case TaskStatus.COMPLETED:
      return "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-900/50";
    case TaskStatus.FAILED:
      return "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-900/50";
    case TaskStatus.RUNNING:
      return "bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-900/50";
    case TaskStatus.WAITING_FOR_USER:
      return "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-900/50";
    case TaskStatus.SKIPPED:
      return "bg-slate-100 dark:bg-slate-700/50 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-700";
    default:
      return "bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-700";
  }
};

export default PlanRunner;
